import React, { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import Header from './Header';
import MagnetButton from '../common/MagnetButton';

const imageUrl =
  'https://media-manager-c.questera.ai/greta-media/b64cc446cdd47675bdad19de596fda49c979fcc46f742bafa38ab384e40234c3cc3101a26c56d32c5dccb06920b38db8/images/aW1hZ2UvanBlZw==/695512a80eca3f037dfcb7c38c0f1b87.jpg';

const tags = [
  { text: '< Powerbanks />', className: 'top-[30%] left-[10%] md:left-[20%]' },
  { text: '< App-Entwicklung />', className: 'top-[25%] right-[10%] md:right-[20%]' },
  { text: '< KI-Assistenten />', className: 'top-[48%] left-[5%] md:left-[15%]' },
  { text: '< Webanwendungen />', className: 'top-[52%] right-[5%] md:right-[15%]' },
  { text: '< Solar & Elektro />', className: 'bottom-[35%] left-[5%] md:left-[22%]' },
  { text: '< Software />', className: 'bottom-[30%] right-[5%] md:right-[22%]' }
];

const SpotlightImage = ({ isMobile }) => {
  const imgRef = useRef(null);
  const cursorX = useMotionValue(-1000);
  const cursorY = useMotionValue(-1000);
  const springConfig = { damping: 25, stiffness: 300, mass: 0.1 };
  const smoothX = useSpring(cursorX, springConfig);
  const smoothY = useSpring(cursorY, springConfig);

  const handleMouseMove = (event) => {
    if (!imgRef.current || isMobile) return;
    const rect = imgRef.current.getBoundingClientRect();
    cursorX.set(event.clientX - rect.left);
    cursorY.set(event.clientY - rect.top);
  };

  const handleMouseLeave = () => {
    cursorX.set(-1000);
    cursorY.set(-1000);
  };

  const maskImageTemplate = useTransform(
    [smoothX, smoothY],
    ([currentX, currentY]) =>
      `radial-gradient(circle 150px at ${currentX}px ${currentY}px, black, transparent)`
  );

  const edgeMask = isMobile
    ? 'linear-gradient(to bottom, black 65%, transparent 100%)'
    : 'linear-gradient(to bottom, black 85%, transparent 100%)';

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className="relative w-[72vw] max-w-[340px] aspect-[4/5] md:w-auto md:max-w-none md:h-[75vh] pointer-events-auto rounded-xl overflow-hidden mb-24 md:mb-0"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      ref={imgRef}
      style={{ maskImage: edgeMask, WebkitMaskImage: edgeMask }}
    >
      {isMobile ? (
        <img
          src={imageUrl}
          alt="Dervis Baygül"
          className="w-full h-full object-cover object-top"
          loading="eager"
          decoding="async"
        />
      ) : (
        <>
          <img
            src={imageUrl}
            alt="Dervis Baygül"
            className="w-full h-full object-cover filter grayscale brightness-[0.2]"
            decoding="async"
          />
          <motion.img
            src={imageUrl}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ maskImage: maskImageTemplate, WebkitMaskImage: maskImageTemplate }}
          />
        </>
      )}
    </motion.div>
  );
};

const HeroSection = () => {
  const containerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const mouseXSpring = useSpring(x, { stiffness: 150, damping: 25, mass: 0.1 });
  const mouseYSpring = useSpring(y, { stiffness: 150, damping: 25, mass: 0.1 });

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const handleMouseMove = (event) => {
    if (!containerRef.current || isMobile) return;
    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    x.set((event.clientX - rect.left - centerX) / centerX);
    y.set((event.clientY - rect.top - centerY) / centerY);
  };

  const handleContactClick = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const textX = useTransform(mouseXSpring, [-1, 1], [8, -8]);
  const textY = useTransform(mouseYSpring, [-1, 1], [8, -8]);
  const tagsX = useTransform(mouseXSpring, [-1, 1], [-20, 20]);
  const tagsY = useTransform(mouseYSpring, [-1, 1], [-20, 20]);

  return (
    <section
      ref={containerRef}
      onMouseMove={handleMouseMove}
      id="home"
      className="relative h-screen bg-[#050505] flex flex-col items-center justify-center overflow-hidden"
    >
      <Header />

      <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-blue-900/10 rounded-full blur-[80px] -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-emerald-900/10 rounded-full blur-[80px] translate-x-1/2 translate-y-1/2 pointer-events-none" />

      <div className="relative w-full max-w-[95vw] mx-auto h-full flex flex-col items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="absolute top-[15vh] left-0 right-0 text-center z-20"
        >
          <span className="text-gray-400 text-sm tracking-[0.2em] uppercase font-medium">
            Willkommen, ich bin Dervis Baygül
          </span>
        </motion.div>

        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-max pointer-events-none select-none z-0 flex justify-center items-center">
          <motion.h1
            style={{ x: textX, y: textY }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            className="text-[12vw] md:text-[14vw] font-black leading-none text-[#1a1a1a] whitespace-nowrap text-center"
          >
            TECHNIK &amp; WEB
          </motion.h1>
        </div>

        <div className="relative z-10 flex items-center justify-center h-full w-full pointer-events-none">
          <SpotlightImage isMobile={isMobile} />
        </div>

        {!isMobile &&
          tags.map((tag, index) => (
            <motion.div
              key={tag.text}
              style={{ x: tagsX, y: tagsY }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 + index * 0.1, duration: 0.6 }}
              className={`absolute ${tag.className} z-30 pointer-events-none`}
            >
              <span className="text-gray-500/80 text-sm md:text-base font-medium tracking-wide whitespace-nowrap font-mono">
                {tag.text}
              </span>
            </motion.div>
          ))}

        <div className="absolute bottom-[14vh] md:bottom-[3vh] left-1/2 -translate-x-1/2 z-20 pointer-events-none flex justify-center items-center w-screen px-3 md:px-5 overflow-visible">
          <motion.h2
            style={{ x: textX, y: textY }}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="w-full text-center text-[clamp(2rem,8.65vw,11rem)] font-black leading-[1.14] tracking-[-0.065em] text-transparent bg-clip-text bg-gradient-to-b from-white via-gray-400 to-gray-600 whitespace-nowrap overflow-visible px-3"
          >
            TECH-UNTERNEHMER
          </motion.h2>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="absolute bottom-6 left-0 w-full px-5 z-40 md:hidden pointer-events-auto"
        >
          <MagnetButton
            onClick={handleContactClick}
            className="group relative flex items-center justify-center w-full py-4 rounded-full border border-white/20 text-white hover:bg-white hover:text-black transition-colors duration-300 overflow-hidden"
          >
            <span className="relative z-10 text-sm font-medium uppercase tracking-widest">
              Kontakt
            </span>
          </MagnetButton>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
